
import { useState, FormEvent, useRef, useEffect, MouseEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    project: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);
  const formCardRef = useRef<HTMLDivElement>(null);

  const projectTypes = ['Residential', 'Commercial', 'Renovation', 'Consultation'];

  // -------------------------------------------------
  // Intersection observer to trigger entrance animation
  // -------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------
  // Mouse move – used for the “spotlight” effect on the form card
  // -------------------------------------------------
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (formCardRef.current) {
      const rect = formCardRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // -------------------------------------------------
  // Form submission – with basic validation & error handling
  // -------------------------------------------------
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Guard: do not allow overly long project description
    if (formData.project.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('projectType', formData.projectType);
      formBody.append('project', formData.project);

      const response = await fetch('https://readdy.ai/api/form/d60tann4klnl5oj67gcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', projectType: '', project: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------
  // Render
  // -------------------------------------------------
  return (
    <section
      ref={sectionRef}
      className="w-full bg-[#0A0A0A] py-20 md:py-28 lg:py-36 relative overflow-hidden"
    >
      {/* Animated Dot Pattern Background */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle, #C9A86C 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Floating Geometric Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 border border-[#C9A86C]/20 rotate-45 animate-pulse" />
      <div className="absolute bottom-32 right-20 w-24 h-24 border border-[#C9A86C]/10 rotate-12" />
      <div
        className="absolute top-1/2 left-1/4 w-4 h-4 bg-[#C9A86C]/30 rounded-full animate-ping"
        style={{ animationDuration: '3s' }}
      />
      <div
        className="absolute top-1/3 right-1/3 w-3 h-3 bg-[#C9A86C]/20 rounded-full animate-ping"
        style={{ animationDuration: '4s', animationDelay: '1s' }}
      />

      {/* Glowing Orbs */}
      <div className="absolute top-40 right-40 w-64 h-64 bg-[#C9A86C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#C9A86C]/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16 relative z-10">
        {/* Section Header */}
        <div
          className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#C9A86C]" />
            <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.3em] uppercase">
              Get In Touch
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#C9A86C]" />
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-4">
            Let&apos;s Build Your
            <span className="block bg-gradient-to-r from-[#C9A86C] to-[#E8D5A3] bg-clip-text text-transparent">
              Vision Together
            </span>
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
            Every masterpiece begins with a conversation. Share your vision, and we&apos;ll bring it
            to life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-12 lg:gap-16">
          {/* Left Side – Contact Info */}
          <div
            className={`lg:col-span-2 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            {/* Contact Cards */}
            <div className="space-y-4 md:space-y-6 mb-10">
              {[
                { icon: 'ri-phone-line', label: 'Call us', value: '08145540554', href: 'tel:08145540554' },
                { icon: 'ri-mail-line', label: 'Email us', value: 'hello@buildfast.ng', href: 'mailto:hello@buildfast.ng' },
                {
                  icon: 'ri-map-pin-line',
                  label: 'Visit us',
                  value: 'Plot 1077, Israel Plaza, Nitel Junction, Wuse 2, Abuja',
                  href: '#'
                }
              ].map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className="group flex items-start gap-4 p-4 md:p-5 bg-white/5 border border-white/10 hover:border-[#C9A86C]/50 hover:bg-[#C9A86C]/5 transition-all duration-500 cursor-pointer relative overflow-hidden"
                >
                  {/* Hover Glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#C9A86C]/0 via-[#C9A86C]/5 to-[#C9A86C]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                  <div className="w-12 h-12 bg-[#C9A86C]/10 border border-[#C9A86C]/30 flex items-center justify-center group-hover:bg-[#C9A86C] group-hover:border-[#C9A86C] transition-all duration-500 flex-shrink-0">
                    <i className={`${item.icon} text-[#C9A86C] text-lg group-hover:text-[#0A0A0A] transition-colors duration-500`} />
                  </div>
                  <div className="relative z-10">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{item.label}</p>
                    <p className="text-white font-medium text-sm md:text-base group-hover:text-[#C9A86C] transition-colors duration-300">
                      {item.value}
                    </p>
                  </div>
                  <i className="ri-arrow-right-up-line text-[#C9A86C] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-auto self-center" />
                </a>
              ))}
            </div>

            {/* Social Links */}
            <div className="mb-10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Follow Us</p>
              <div className="flex gap-3">
                {[
                  { icon: 'ri-instagram-line', href: 'https://instagram.com' },
                  { icon: 'ri-linkedin-line', href: 'https://linkedin.com' },
                  { icon: 'ri-twitter-x-line', href: 'https://twitter.com' },
                  { icon: 'ri-facebook-line', href: 'https://facebook.com' }
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/60 hover:border-[#C9A86C] hover:text-[#C9A86C] hover:bg-[#C9A86C]/10 transition-all duration-300 cursor-pointer group"
                  >
                    <i className={`${social.icon} text-lg group-hover:scale-110 transition-transform duration-300`} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { number: '24h', label: 'Response Time' },
                { number: '100+', label: 'Projects Completed' }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="p-4 border border-white/10 bg-white/5 text-center group hover:border-[#C9A86C]/30 transition-all duration-300"
                >
                  <p className="text-2xl md:text-3xl font-serif text-[#C9A86C] mb-1 group-hover:scale-110 transition-transform duration-300">
                    {stat.number}
                  </p>
                  <p className="text-white/40 text-xs uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side – Form */}
          <div
            ref={formCardRef}
            onMouseMove={handleMouseMove}
            className={`lg:col-span-3 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 p-6 md:p-8 lg:p-10 overflow-hidden group">
              {/* Spotlight Effect */}
              <div
                className="absolute w-64 h-64 bg-[#C9A86C]/10 rounded-full blur-3xl pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                style={{
                  left: mousePosition.x - 128,
                  top: mousePosition.y - 128
                }}
              />

              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#C9A86C]/30" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#C9A86C]/30" />

              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-serif text-white mb-2">Start Your Project</h3>
                <p className="text-white/40 text-sm mb-8">
                  Fill in the details and we&apos;ll get back to you within 24 hours.
                </p>

                <form
                  id="contact-form"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Name & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name Field */}
                    <div className="relative">
                      <label
                        className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                          focusedField === 'name' || formData.name
                            ? 'top-1 text-xs text-[#C9A86C]'
                            : 'top-4 text-sm text-white/40'
                        }`}
                      >
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className="w-full bg-white/5 border-2 border-white/10 px-4 pt-6 pb-3 text-sm text-white focus:outline-none focus:border-[#C9A86C] focus:bg-[#C9A86C]/5 transition-all duration-300"
                      />
                      <div
                        className={`absolute bottom-0 left-0 h-0.5 bg-[#C9A86C] transition-all duration-500 ${
                          focusedField === 'name' ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>

                    {/* Phone Field */}
                    <div className="relative">
                      <label
                        className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                          focusedField === 'phone' || formData.phone
                            ? 'top-1 text-xs text-[#C9A86C]'
                            : 'top-4 text-sm text-white/40'
                        }`}
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        className="w-full bg-white/5 border-2 border-white/10 px-4 pt-6 pb-3 text-sm text-white focus:outline-none focus:border-[#C9A86C] focus:bg-[#C9A86C]/5 transition-all duration-300"
                      />
                      <div
                        className={`absolute bottom-0 left-0 h-0.5 bg-[#C9A86C] transition-all duration-500 ${
                          focusedField === 'phone' ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <label
                      className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                        focusedField === 'email' || formData.email
                          ? 'top-1 text-xs text-[#C9A86C]'
                          : 'top-4 text-sm text-white/40'
                      }`}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full bg-white/5 border-2 border-white/10 px-4 pt-6 pb-3 text-sm text-white focus:outline-none focus:border-[#C9A86C] focus:bg-[#C9A86C]/5 transition-all duration-300"
                    />
                    <div
                      className={`absolute bottom-0 left-0 h-0.5 bg-[#C9A86C] transition-all duration-500 ${
                        focusedField === 'email' ? 'w-full' : 'w-0'
                      }`}
                    />
                  </div>

                  {/* Project Type Selector */}
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Project Type</p>
                    <div className="flex flex-wrap gap-3">
                      {projectTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({ ...formData, projectType: type })}
                          className={`px-5 py-2.5 text-sm border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                            formData.projectType === type
                              ? 'bg-[#C9A86C] border-[#C9A86C] text-[#0A0A0A] font-medium'
                              : 'bg-transparent border-white/20 text-white/60 hover:border-[#C9A86C]/50 hover:text-[#C9A86C]'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Project Description */}
                  <div className="relative">
                    <label
                      className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                        focusedField === 'project' || formData.project
                          ? 'top-1 text-xs text-[#C9A86C]'
                          : 'top-4 text-sm text-white/40'
                      }`}
                    >
                      Tell us about your project
                    </label>
                    <textarea
                      name="project"
                      value={formData.project}
                      onChange={(e) =>
                        setFormData({ ...formData, project: e.target.value.slice(0, 500) })
                      }
                      onFocus={() => setFocusedField('project')}
                      onBlur={() => setFocusedField(null)}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full bg-white/5 border-2 border-white/10 px-4 pt-6 pb-3 text-sm text-white focus:outline-none focus:border-[#C9A86C] focus:bg-[#C9A86C]/5 transition-all duration-300 resize-none"
                    />
                    <div
                      className={`absolute bottom-0 left-0 h-0.5 bg-[#C9A86C] transition-all duration-500 ${
                        focusedField === 'project' ? 'w-full' : 'w-0'
                      }`}
                    />
                    <p className="text-white/30 text-xs mt-2 text-right">
                      {formData.project.length}/500
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-[#C9A86C] to-[#E8D5A3] text-[#0A0A0A] font-medium tracking-wide hover:from-[#E8D5A3] hover:to-[#C9A86C] transition-all duration-500 disabled:opacity-50 whitespace-nowrap cursor-pointer text-sm md:text-base flex items-center justify-center gap-3 group relative overflow-hidden"
                  >
                    <span className="relative z-10">{isSubmitting ? 'Sending...' : 'Send Inquiry'}</span>
                    <i
                      className={`ri-arrow-right-line relative z-10 transition-transform duration-300 ${
                        isSubmitting ? 'animate-pulse' : 'group-hover:translate-x-2'
                      }`}
                    />
                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 text-green-400 text-sm text-center flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-line text-lg" />
                      Thank you! We&apos;ll be in touch within 24 hours.
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center flex items-center justify-center gap-2">
                      <i className="ri-error-warning-line text-lg" />
                      Something went wrong. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
